import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import * as FaIcons from 'react-icons/fa';
import SafeIcon from './common/SafeIcon';
import './App.css';

const { 
  FiGlobe, FiPhone, FiMail, FiMapPin, FiMenu, FiX, FiHeart, 
  FiUsers, FiShield, FiCheckCircle, FiArrowRight, FiCalendar, 
  FiUser, FiSend, FiLock, FiMessageSquare, FiStar, FiMessageCircle, 
  FiDollarSign, FiBriefcase
} = FiIcons;
const { FaWhatsapp, FaPlane, FaCompass } = FaIcons;

function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: '',
    destination: '',
    travelDate: '',
    travelers: '',
    message: ''
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const destinations = [
    {
      name: "Dubai",
      description: "Luxury & adventure",
      image: "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
    },
    {
      name: "Bali",
      description: "Nature & relaxation",
      image: "https://images.unsplash.com/photo-1537953773345-d172ccf13cf1?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
    },
    {
      name: "Thailand",
      description: "Beaches & nightlife",
      image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
    },
    {
      name: "Singapore",
      description: "Modern city vibes",
      image: "https://images.unsplash.com/photo-1525625293386-3f8f99389edd?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
    },
    {
      name: "Europe",
      description: "Culture & romance",
      image: "https://images.unsplash.com/photo-1467269204594-9661b134dd2b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
    }
  ];

  const features = [
    {
      icon: FiHeart,
      title: "Personalized Trips",
      description: "Designed around your comfort"
    },
    {
      icon: FiUsers,
      title: "Friendly Guidance",
      description: "Clear, honest support at every step"
    },
    {
      icon: FiShield,
      title: "Trusted Hotels & Partners",
      description: "Safe & quality stays"
    },
    {
      icon: FiCheckCircle,
      title: "Hassle-Free Planning",
      description: "We manage everything"
    }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitted(true);
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({
        fullName: '',
        phone: '',
        email: '',
        destination: '',
        travelDate: '',
        travelers: '',
        message: ''
      });
    }, 5000);
  };

  const openWhatsApp = () => {
    const message = encodeURIComponent("Hi Fresco Travel, I want to plan a trip.");
    window.open(`https://wa.me/919354437602?text=${message}`, '_blank');
  };

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
    setIsMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-lg py-2' : 'bg-transparent py-4'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2 cursor-pointer" onClick={() => scrollToSection('home')}>
              <div className="relative">
                <SafeIcon icon={FiGlobe} className="text-3xl text-yellow-500" />
                <SafeIcon icon={FaPlane} className="absolute -top-1 -right-1 text-sm text-green-500" />
              </div>
              <div className="flex flex-col">
                <span className="text-xl font-bold text-gray-800 leading-none">Fresco Travel</span>
                <span className="text-[10px] text-gray-500 font-medium tracking-widest uppercase">Explore The World</span>
              </div>
            </div>

            <nav className="hidden md:flex items-center space-x-8">
              {['Home', 'About', 'Packages'].map((item) => (
                <button 
                  key={item}
                  onClick={() => scrollToSection(item.toLowerCase())} 
                  className="text-gray-700 font-medium hover:text-green-600 transition-colors relative group"
                >
                  {item}
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-green-500 transition-all group-hover:w-full"></span>
                </button>
              ))}
              <button 
                onClick={openWhatsApp}
                className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-white px-7 py-2.5 rounded-full font-bold shadow-md hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200"
              >
                Contact Us
              </button>
            </nav>

            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="md:hidden text-gray-700">
              <SafeIcon icon={isMenuOpen ? FiX : FiMenu} className="text-2xl" />
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section id="home" className="relative h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center scale-105"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1488646953014-85cb44e25828?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/60" />
        
        <div className="relative z-10 text-center text-white max-w-5xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            className="mb-6 inline-flex items-center space-x-2 bg-white/10 backdrop-blur-md px-4 py-2 rounded-full border border-white/20"
          >
            <SafeIcon icon={FiStar} className="text-yellow-400" />
            <span className="text-sm font-semibold tracking-wide uppercase">Your Trusted Travel Partner</span>
          </motion.div>
          
          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-5xl md:text-7xl font-extrabold mb-6 leading-tight"
          >
            Your Journey, <br />
            <span className="gradient-text">Our Happiness</span>
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-lg md:text-2xl mb-10 max-w-3xl mx-auto text-gray-200 leading-relaxed font-light"
          >
            We create stress-free, comfortable, and unforgettable travel experiences — from quick getaways to international holidays.
          </motion.p>
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="flex flex-col sm:flex-row gap-5 justify-center items-center"
          >
            <button 
              onClick={() => scrollToSection('packages')}
              className="group bg-white text-gray-900 px-10 py-4 rounded-full text-lg font-bold hover:bg-yellow-400 transition-all duration-300 flex items-center space-x-2"
            >
              <span>Explore Packages</span>
              <SafeIcon icon={FiArrowRight} className="group-hover:translate-x-1 transition-transform" />
            </button>
            <button 
              onClick={() => scrollToSection('enquiry')}
              className="bg-transparent border-2 border-white/50 hover:border-white text-white px-10 py-4 rounded-full text-lg font-bold backdrop-blur-sm transition-all duration-300"
            >
              Book Your Trip
            </button>
          </motion.div>
        </div>
      </section>

      {/* About Us Section */}
      <section id="about" className="py-24 bg-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="absolute -top-10 -left-10 w-40 h-40 bg-yellow-100 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob"></div>
              <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-green-100 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000"></div>
              <img 
                src="https://images.unsplash.com/photo-1469474968028-56623f02e42e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                alt="Happy travelers"
                className="relative rounded-[2.5rem] shadow-2xl z-10"
              />
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <span className="text-green-600 font-bold tracking-widest uppercase text-sm mb-4 block">Our Story</span>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-8 leading-tight">
                The Heart Behind <br />Our Journeys
              </h2>
              <div className="space-y-6 text-gray-600 text-lg leading-relaxed">
                <p>At Fresco Travel, travel is not just about destinations — it's about emotions, comfort, and care.</p>
                <p>We design personalized holidays for couples, families, and groups, handling everything from flights to hotels so you can travel worry-free.</p>
                <div className="pt-4">
                  <span className="inline-block bg-yellow-400/20 text-yellow-700 px-6 py-3 rounded-2xl font-bold text-xl">
                    Travel with people who truly care.
                  </span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Corporate Travel Package Section (NEW) */}
      <section className="py-24 bg-[#fbbf24] overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            
            {/* Left Side: Image Collage */}
            <motion.div 
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="relative h-[650px] md:h-[700px]"
            >
              {/* Large Vertical Image */}
              <div className="absolute left-0 top-1/2 -translate-y-1/2 w-4/5 h-4/5 z-10">
                <img 
                  src="https://images.unsplash.com/photo-1556761175-b413da4baf72?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" 
                  alt="Corporate Professionals" 
                  className="w-full h-full object-cover rounded-[3rem] shadow-2xl border-8 border-white group"
                />
              </div>

              {/* Three Small Rounded Top Images */}
              <div className="absolute top-0 right-0 flex space-x-3 z-20">
                <img 
                  src="https://images.unsplash.com/photo-1530521954074-e64f6810b32d?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80" 
                  alt="Airport" 
                  className="w-20 h-20 md:w-28 md:h-28 rounded-2xl md:rounded-3xl object-cover shadow-xl border-4 border-white"
                />
                <img 
                  src="https://images.unsplash.com/photo-1544644181-1484b3fdfc62?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80" 
                  alt="Business Travel" 
                  className="w-20 h-20 md:w-28 md:h-28 rounded-2xl md:rounded-3xl object-cover shadow-xl border-4 border-white"
                />
                <img 
                  src="https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80" 
                  alt="Car Pickup" 
                  className="w-20 h-20 md:w-28 md:h-28 rounded-2xl md:rounded-3xl object-cover shadow-xl border-4 border-white"
                />
              </div>

              {/* Medium Image Below */}
              <div className="absolute bottom-0 right-4 w-1/2 h-1/3 z-20">
                <img 
                  src="https://images.unsplash.com/photo-1566073771259-6a8506099945?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80" 
                  alt="Hotel Check-in" 
                  className="w-full h-full object-cover rounded-[2rem] shadow-2xl border-8 border-white"
                />
              </div>
            </motion.div>

            {/* Right Side: Content */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="lg:pl-10"
            >
              <div className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-md px-4 py-2 rounded-full border border-white/30 text-white mb-6">
                <SafeIcon icon={FiBriefcase} />
                <span className="text-xs font-bold tracking-[0.2em] uppercase">Our Package</span>
              </div>
              
              <h2 className="text-4xl md:text-6xl font-extrabold text-gray-900 mb-8 leading-tight">
                Your Corporate <br />Travel Partner
              </h2>
              
              <p className="text-gray-800 text-lg md:text-xl leading-relaxed mb-10 font-medium opacity-90">
                We provide end-to-end corporate travel solutions including flights, hotels, transfers, and itinerary management to ensure your team travels efficiently, comfortably, and on schedule.
              </p>
              
              <button 
                onClick={() => scrollToSection('enquiry')}
                className="bg-gray-900 text-white px-10 py-5 rounded-full text-lg font-extrabold hover:bg-gray-800 transform hover:-translate-y-1 transition-all duration-300 shadow-xl flex items-center space-x-3 active:scale-95"
              >
                <span>Get Free Consultation</span>
                <SafeIcon icon={FiArrowRight} />
              </button>
            </motion.div>

          </div>
        </div>
      </section>

      {/* Why Choose Us Section 1 (Grid) */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Core Values</h2>
            <div className="w-20 h-1.5 bg-green-500 mx-auto rounded-full"></div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white p-10 rounded-3xl shadow-sm hover:shadow-xl transition-all duration-500 group border border-gray-100"
              >
                <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center mb-8 group-hover:bg-green-500 transition-colors duration-500">
                  <SafeIcon icon={feature.icon} className="text-3xl text-green-600 group-hover:text-white transition-colors duration-500" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Popular Destinations Section */}
      <section id="packages" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
            <div className="max-w-2xl">
              <span className="text-green-600 font-bold tracking-widest uppercase text-sm mb-4 block">World to Discover</span>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900">Popular Destinations</h2>
            </div>
            <p className="text-gray-500 text-lg max-w-xs">Handpicked locations for your next unforgettable escape.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
            {destinations.map((destination, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="group relative h-[450px] rounded-[2.5rem] overflow-hidden shadow-lg"
              >
                <img 
                  src={destination.image}
                  alt={destination.name}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-8 transform transition-transform duration-500 group-hover:-translate-y-2">
                  <h3 className="text-3xl font-bold text-white mb-2">{destination.name}</h3>
                  <p className="text-gray-300 mb-6 font-light">{destination.description}</p>
                  <button 
                    onClick={() => {
                      setFormData(prev => ({ ...prev, destination: destination.name }));
                      scrollToSection('enquiry');
                    }}
                    className="bg-white text-gray-900 px-6 py-3 rounded-full font-bold text-sm flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-all duration-500 delay-100"
                  >
                    <span>View Package</span>
                    <SafeIcon icon={FiArrowRight} />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US (Two Column) */}
      <section className="py-28 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            
            {/* Left Column: Image */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="relative rounded-[3rem] overflow-hidden shadow-[0_30px_60px_-15px_rgba(0,0,0,0.2)]">
                <img 
                  src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80"
                  alt="Our travel experts assisting customers"
                  className="w-full aspect-[4/5] object-cover hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-green-900/40 to-transparent mix-blend-overlay"></div>
                
                {/* Floating Experience Card */}
                <motion.div 
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.5 }}
                  className="absolute bottom-10 left-10 right-10 bg-white/90 backdrop-blur-md p-6 rounded-3xl shadow-xl border border-white/20"
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-yellow-500 rounded-full flex items-center justify-center text-white font-bold text-xl">
                      10+
                    </div>
                    <div>
                      <p className="font-bold text-gray-900">Years of Experience</p>
                      <p className="text-sm text-gray-600">Making dreams come true</p>
                    </div>
                  </div>
                </motion.div>
              </div>
              
              {/* Decorative circle */}
              <div className="absolute -z-10 -top-10 -left-10 w-64 h-64 bg-green-50 rounded-full"></div>
            </motion.div>

            {/* Right Column: Content */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <span className="text-green-600 font-bold tracking-widest uppercase text-xs mb-4 block bg-green-50 w-fit px-4 py-1.5 rounded-full">
                WHY CHOOSE US
              </span>
              <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-8 leading-tight">
                Why Travel With <br />
                <span className="text-green-600">Fresco Travel?</span>
              </h2>
              <p className="text-gray-600 text-lg mb-10 leading-relaxed">
                We plan every trip with care, honesty, and personal attention — just like we would for our own journeys. We also specialize in corporate tour packages designed to be smooth, professional, and well-managed.
              </p>
              
              <div className="space-y-8">
                {[
                  {
                    title: "Friendly Travel Guidance",
                    desc: "Think of us as your travel buddy — we explain things simply, help you choose better, and guide you at every step.",
                    icon: FiMessageCircle,
                    color: "bg-blue-50 text-blue-600"
                  },
                  {
                    title: "Safe & Verified Arrangements",
                    desc: "Hotels, transports, and tours are chosen with care. We work only with trusted partners so you travel with confidence.",
                    icon: FiShield,
                    color: "bg-green-50 text-green-600"
                  },
                  {
                    title: "Budget That Feels Right",
                    desc: "Whether you want luxury, mid-range, or economical — we plan thoughtfully to match your comfort and budget.",
                    icon: FiDollarSign,
                    color: "bg-yellow-50 text-yellow-600"
                  },
                  {
                    title: "Support When You Need It",
                    desc: "Have a question during your trip? Need quick help? We’re just a message away — calm, supportive, and always available.",
                    icon: FaWhatsapp,
                    color: "bg-emerald-50 text-emerald-600"
                  }
                ].map((item, idx) => (
                  <motion.div 
                    key={idx}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.1 }}
                    className="flex items-start space-x-6 group"
                  >
                    <div className={`flex-shrink-0 w-14 h-14 rounded-2xl ${item.color} flex items-center justify-center text-2xl shadow-sm group-hover:shadow-md transition-all duration-300`}>
                      <SafeIcon icon={item.icon} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                      <p className="text-gray-500 leading-relaxed text-sm md:text-base">{item.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Enquiry / Trip Planning Form Section */}
      <section id="enquiry" className="py-28 relative overflow-hidden bg-gradient-to-br from-white via-green-50/20 to-yellow-50/30">
        <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-green-100/30 rounded-full blur-3xl -mr-20 -mt-20"></div>
        <div className="absolute bottom-0 left-0 w-1/4 h-1/4 bg-yellow-100/30 rounded-full blur-3xl -ml-20 -mb-20"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-12 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="lg:col-span-5"
            >
              <div className="inline-flex items-center space-x-2 bg-green-100 text-green-700 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-6">
                <SafeIcon icon={FaCompass} />
                <span>Trip Planner</span>
              </div>
              <h2 className="text-4xl md:text-6xl font-extrabold text-gray-900 mb-8 leading-[1.1]">
                Plan Your Trip <br />
                <span className="text-green-600">With Us</span>
              </h2>
              <p className="text-gray-600 text-xl mb-10 leading-relaxed font-light">
                Not sure where to start? Tell us your travel plans and preferences, and our travel experts will design a personalized holiday that matches your <span className="text-gray-900 font-medium">budget, comfort, and style</span>.
              </p>
              
              <div className="grid gap-6 mb-12">
                {[
                  { text: "Free travel consultation", icon: FiMessageSquare },
                  { text: "Personalized itinerary", icon: FiMapPin },
                  { text: "Quick response via call or WhatsApp", icon: FaWhatsapp }
                ].map((item, idx) => (
                  <motion.div 
                    key={idx}
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.1 * idx }}
                    className="flex items-center space-x-5 group"
                  >
                    <div className="flex-shrink-0 w-12 h-12 rounded-2xl bg-white shadow-sm border border-gray-100 flex items-center justify-center group-hover:bg-green-500 transition-all duration-300">
                      <SafeIcon icon={item.icon} className="text-green-600 text-xl group-hover:text-white transition-colors" />
                    </div>
                    <span className="text-gray-800 font-semibold text-lg">{item.text}</span>
                  </motion.div>
                ))}
              </div>

              <div className="flex items-center space-x-3 text-gray-400 bg-white/40 backdrop-blur-sm w-fit px-6 py-3 rounded-2xl border border-white/60 shadow-sm">
                <SafeIcon icon={FiLock} className="text-green-500" />
                <span className="text-sm font-medium">100% safe & secure. No spam, no hidden charges.</span>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="lg:col-span-7"
            >
              <div className="bg-white p-8 md:p-12 rounded-[3rem] shadow-[0_20px_50px_rgba(0,0,0,0.08)] border border-gray-50 relative overflow-hidden">
                <AnimatePresence mode="wait">
                  {isSubmitted ? (
                    <motion.div 
                      key="success"
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="text-center py-16"
                    >
                      <div className="w-24 h-24 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-8 relative">
                        <SafeIcon icon={FiCheckCircle} className="text-5xl text-green-600 relative z-10" />
                      </div>
                      <h3 className="text-3xl font-extrabold text-gray-900 mb-4">Thank you!</h3>
                      <p className="text-gray-500 text-lg max-w-xs mx-auto">Our travel expert will contact you shortly to plan your perfect getaway.</p>
                      <button 
                        onClick={() => setIsSubmitted(false)}
                        className="mt-10 text-green-600 font-bold hover:underline"
                      >
                        Send another enquiry
                      </button>
                    </motion.div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-1.5">
                          <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Full Name *</label>
                          <div className="relative group">
                            <SafeIcon icon={FiUser} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-green-500 transition-colors" />
                            <input 
                              type="text" 
                              name="fullName"
                              required
                              placeholder="e.g. John Doe"
                              value={formData.fullName}
                              onChange={handleInputChange}
                              className="w-full pl-12 pr-4 py-4 bg-gray-50/50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-green-500/10 focus:border-green-500 focus:bg-white outline-none transition-all text-gray-800 font-medium"
                            />
                          </div>
                        </div>
                        <div className="space-y-1.5">
                          <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Phone Number *</label>
                          <div className="relative group">
                            <SafeIcon icon={FiPhone} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-green-500 transition-colors" />
                            <input 
                              type="tel" 
                              name="phone"
                              required
                              placeholder="+91 9354 437 602"
                              value={formData.phone}
                              onChange={handleInputChange}
                              className="w-full pl-12 pr-4 py-4 bg-gray-50/50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-green-500/10 focus:border-green-500 focus:bg-white outline-none transition-all text-gray-800 font-medium"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Email Address</label>
                        <div className="relative group">
                          <SafeIcon icon={FiMail} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-green-500 transition-colors" />
                          <input 
                            type="email" 
                            name="email"
                            placeholder="frescotravel@gmail.com"
                            value={formData.email}
                            onChange={handleInputChange}
                            className="w-full pl-12 pr-4 py-4 bg-gray-50/50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-green-500/10 focus:border-green-500 focus:bg-white outline-none transition-all text-gray-800 font-medium"
                          />
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-1.5">
                          <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Destination</label>
                          <div className="relative group">
                            <SafeIcon icon={FiMapPin} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-green-500 transition-colors" />
                            <input 
                              type="text" 
                              name="destination"
                              placeholder="Where to go?"
                              value={formData.destination}
                              onChange={handleInputChange}
                              className="w-full pl-12 pr-4 py-4 bg-gray-50/50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-green-500/10 focus:border-green-500 focus:bg-white outline-none transition-all text-gray-800 font-medium"
                            />
                          </div>
                        </div>
                        <div className="space-y-1.5">
                          <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Travel Date</label>
                          <div className="relative group">
                            <SafeIcon icon={FiCalendar} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-green-500 transition-colors" />
                            <input 
                              type="date" 
                              name="travelDate"
                              value={formData.travelDate}
                              onChange={handleInputChange}
                              className="w-full pl-12 pr-4 py-4 bg-gray-50/50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-green-500/10 focus:border-green-500 focus:bg-white outline-none transition-all text-gray-800 font-medium"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Number of Travelers</label>
                        <div className="relative group">
                          <SafeIcon icon={FiUsers} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-green-500 transition-colors" />
                          <input 
                            type="text" 
                            name="travelers"
                            placeholder="e.g. 2 Adults, 1 Child"
                            value={formData.travelers}
                            onChange={handleInputChange}
                            className="w-full pl-12 pr-4 py-4 bg-gray-50/50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-green-500/10 focus:border-green-500 focus:bg-white outline-none transition-all text-gray-800 font-medium"
                          />
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Special Requirements</label>
                        <textarea 
                          name="message" 
                          rows="3" 
                          placeholder="Tell us about your dream trip..." 
                          value={formData.message} 
                          onChange={handleInputChange} 
                          className="w-full px-5 py-4 bg-gray-50/50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-green-500/10 focus:border-green-500 focus:bg-white outline-none transition-all text-gray-800 font-medium resize-none" 
                        ></textarea>
                      </div>
                      <button 
                        type="submit" 
                        className="w-full bg-gradient-to-r from-green-500 via-green-600 to-emerald-700 text-white py-5 rounded-[1.5rem] font-extrabold text-lg shadow-[0_15px_30px_rgba(16,185,129,0.3)] hover:shadow-[0_20px_40px_rgba(16,185,129,0.4)] transform hover:-translate-y-1 transition-all duration-300 flex items-center justify-center space-x-3 active:scale-95" 
                      >
                        <span>Get Free Travel Consultation</span>
                        <SafeIcon icon={FiSend} className="text-xl" />
                      </button>
                    </form>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="bg-[#0f172a] text-white pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-24 mb-20">
            <div className="lg:col-span-4">
              <div className="flex items-center space-x-2 mb-8">
                <div className="relative">
                  <SafeIcon icon={FiGlobe} className="text-3xl text-yellow-500" />
                  <SafeIcon icon={FaPlane} className="absolute -top-1 -right-1 text-sm text-green-500" />
                </div>
                <div className="flex flex-col">
                  <span className="text-2xl font-bold leading-none">Fresco Travel</span>
                  <span className="text-[10px] text-gray-400 font-medium tracking-widest uppercase">Explore The World</span>
                </div>
              </div>
              <p className="text-gray-400 text-lg leading-relaxed mb-8">
                Crafting memories, not just itineraries. We are dedicated to providing you with the most stress-free and luxurious travel experiences across the globe.
              </p>
              <div className="flex space-x-4">
                {[FiGlobe, FiUsers, FiMail].map((Icon, i) => (
                  <button key={i} className="w-12 h-12 rounded-2xl bg-white/5 flex items-center justify-center hover:bg-green-500 transition-all duration-300">
                    <SafeIcon icon={Icon} className="text-xl" />
                  </button>
                ))}
              </div>
            </div>
            
            <div className="lg:col-span-4">
              <h3 className="text-xl font-bold mb-8 flex items-center space-x-2">
                <span className="w-6 h-1 bg-green-500 rounded-full"></span>
                <span>Contact Details</span>
              </h3>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 rounded-xl bg-green-500/10 flex items-center justify-center flex-shrink-0">
                    <SafeIcon icon={FiPhone} className="text-green-500" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-bold tracking-widest mb-1">Call Us</p>
                    <p className="text-lg font-semibold">9354437602</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 rounded-xl bg-yellow-500/10 flex items-center justify-center flex-shrink-0">
                    <SafeIcon icon={FiMail} className="text-yellow-500" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-bold tracking-widest mb-1">Email Us</p>
                    <p className="text-lg font-semibold break-all">frescotravel@gmail.com</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                    <SafeIcon icon={FiMapPin} className="text-blue-500" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-bold tracking-widest mb-1">Our Office</p>
                    <p className="text-gray-300 leading-relaxed">
                      Tower B2, Spaze I-Tech Park, Sector 49, Sohna Road, Gurgaon, Haryana – 122018
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="lg:col-span-4">
              <h3 className="text-xl font-bold mb-8 flex items-center space-x-2">
                <span className="w-6 h-1 bg-yellow-500 rounded-full"></span>
                <span>Quick Navigation</span>
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {['Home', 'About', 'Packages', 'Enquiry'].map((item) => (
                  <button 
                    key={item} 
                    onClick={() => scrollToSection(item.toLowerCase())} 
                    className="flex items-center space-x-2 text-gray-400 hover:text-white transition-colors group"
                  >
                    <SafeIcon icon={FiArrowRight} className="text-xs group-hover:translate-x-1 transition-transform" />
                    <span>{item}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
          
          <div className="border-t border-white/5 pt-12 flex flex-col md:flex-row justify-between items-center gap-6">
            <p className="text-gray-500 text-sm">© 2024 Fresco Travel. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* WhatsApp Floating Button */}
      <motion.button 
        initial={{ scale: 0 }} 
        animate={{ scale: 1 }} 
        transition={{ delay: 1, type: "spring", stiffness: 260, damping: 20 }} 
        onClick={openWhatsApp} 
        className="fixed bottom-8 right-8 bg-[#25D366] text-white w-16 h-16 rounded-full shadow-[0_10px_30px_rgba(37,211,102,0.4)] hover:shadow-[0_15px_40px_rgba(37,211,102,0.5)] transform hover:scale-110 active:scale-95 transition-all duration-300 z-50 flex items-center justify-center group" 
      >
        <SafeIcon icon={FaWhatsapp} className="text-3xl" />
        <span className="absolute right-full mr-5 bg-white text-gray-900 px-4 py-2 rounded-xl text-sm font-bold shadow-xl opacity-0 translate-x-10 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300 pointer-events-none whitespace-nowrap">Plan Your Trip</span>
      </motion.button>
    </div>
  );
}

export default App;